/**
 * Personel "Görevlerim" kartı — kendi kendine veri çeker (AvansKarti gibi).
 * Bugün geçerli + devam eden görevleri, tamamlama durumuyla gösterir.
 *
 * Parça C: zaman aralığı geçti + işaretlenmedi → "Kaçırıldı" rozeti.
 *  Otomatik ceza YOK; butonlar AÇIK kalır (gecikmeli de olsa yapılabilir — KARAR #25).
 */

import { useEffect, useState, useCallback } from 'react'
import {
  ClipboardList, CheckCircle2, XCircle, Clock, Hourglass, AlertTriangle,
} from 'lucide-react'
import {
  personelBugunkuGorevleri, kacirildiMi, gorevTamamlanabilirMi,
  type PersonelGorevSatiri, type GorevDoc,
} from '@/lib/services/gorev-service'
import { GorevTamamlaModal } from './GorevTamamlaModal'
import type { KullaniciPersonel } from '@/types'

interface Props {
  personel: KullaniciPersonel
}

export function GorevlerimKarti({ personel }: Props) {
  const [satirlar, setSatirlar] = useState<PersonelGorevSatiri[]>([])
  const [yukleniyor, setYukleniyor] = useState(true)
  const [modal, setModal] = useState<{
    gorev: GorevDoc
    tarih: string
    mod: 'tamamla' | 'yapilamadi'
    gecikmeli?: boolean
  } | null>(null)

  const yukle = useCallback(async () => {
    try {
      setYukleniyor(true)
      const liste = await personelBugunkuGorevleri(personel)
      setSatirlar(liste)
    } catch (err) {
      console.error(err)
    } finally {
      setYukleniyor(false)
    }
  }, [personel])

  useEffect(() => { yukle() }, [yukle])

  const acikSayi = satirlar.filter((s) => {
    const durum = s.tamamlama?.durum as string | undefined
    const kacti = kacirildiMi(s.gorev, s.tarih, s.tamamlama ?? null)
    return (
      !s.tamamlama ||
      durum === 'kacirildi' ||
      durum === 'reddedildi' ||
      kacti
    )
  }).length

  if (yukleniyor) {
    return (
      <div className="card">
        <div className="flex items-center gap-2 mb-2">
          <ClipboardList className="w-4 h-4 text-gray-500" />
          <h2 className="font-bold text-gray-900 text-sm">Görevlerim</h2>
        </div>
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600"></div>
        </div>
      </div>
    )
  }

  if (satirlar.length === 0) {
    return (
      <div className="card">
        <div className="flex items-center gap-2 mb-1">
          <ClipboardList className="w-4 h-4 text-gray-500" />
          <h2 className="font-bold text-gray-900 text-sm">Görevlerim</h2>
        </div>
        <p className="text-xs text-gray-500 py-2">Bugün için görevin yok 👍</p>
      </div>
    )
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-gray-500" />
          <h2 className="font-bold text-gray-900 text-sm">Görevlerim</h2>
        </div>
        {acikSayi > 0 && (
          <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
            {acikSayi} bekliyor
          </span>
        )}
      </div>

      <div className="space-y-2">
        {satirlar.map(({ gorev, tamamlama, tarih }) => {
          const durum = tamamlama?.durum
          const yapildi = durum === 'tamamlandi'
          const yapilamadi = durum === 'yapilamadi'
          const reddedildi = (durum as string) === 'reddedildi'
          const onayda = (!!tamamlama?.onayBekliyor || durum === 'beklemede') && !reddedildi
          // Süresi geçti + çözülmemiş → kaçırıldı (türetilmiş, KARAR #24).
          const kacti = !onayda && !yapildi && !yapilamadi &&
            kacirildiMi(gorev, tarih, tamamlama ?? null)
          // Reddedilen / kaçırılan / hiç yapılmamış → tekrar yapılabilir (açık).
          const acik = !tamamlama || reddedildi || kacti ||
            (durum as string) === 'kacirildi'
          // Tamamlama zaman penceresi (saat aralığı bitince "yapıldı" kapanır).
          const pencere = gorevTamamlanabilirMi(gorev, tarih)

          return (
            <div
              key={gorev.id}
              className={`border rounded-xl p-3 ${
                yapildi ? 'bg-green-50 border-green-200'
                  : reddedildi ? 'bg-orange-50 border-orange-300'
                  : kacti ? 'bg-red-50 border-red-300'
                  : yapilamadi ? 'bg-red-50 border-red-200'
                  : onayda ? 'bg-blue-50 border-blue-200'
                  : 'bg-white border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-sm">{gorev.baslik}</p>
                  <p className="text-xs text-gray-600 mt-0.5">{gorev.aciklama}</p>
                  {gorev.zamanAraligi && (
                    <p className="text-[11px] text-gray-500 mt-1 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {gorev.zamanAraligi.baslangic}–{gorev.zamanAraligi.bitis}
                    </p>
                  )}
                </div>
                {yapildi && <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />}
                {yapilamadi && <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />}
                {onayda && <Hourglass className="w-5 h-5 text-blue-600 flex-shrink-0" />}
                {reddedildi && <XCircle className="w-5 h-5 text-orange-600 flex-shrink-0" />}
                {kacti && <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />}
              </div>

              {onayda && (
                <p className="text-xs text-blue-700 mt-2">⏳ Onay bekliyor</p>
              )}
              {reddedildi && (
                <div className="text-xs text-orange-800 mt-2 bg-orange-100 rounded-lg p-2">
                  <p className="font-semibold">⚠️ Reddedildi — tekrar yapman gerekiyor</p>
                  {tamamlama?.redSebebi && (
                    <p className="mt-1 italic">"{tamamlama.redSebebi}"</p>
                  )}
                </div>
              )}
              {kacti && (
                <div className="text-xs text-red-800 mt-2 bg-red-100 rounded-lg p-2">
                  <p className="font-semibold">⏰ Süresi geçti — istersen yine de tamamlayabilirsin (geç işaretlenir).</p>
                </div>
              )}
              {yapilamadi && (
                <div className="mt-2">
                  {tamamlama?.aciklama && (
                    <p className="text-xs text-red-700 italic">"{tamamlama.aciklama}"</p>
                  )}
                  {(pencere.olur || pencere.sebep === 'gecti') && (
                    <button
                      onClick={() => setModal({ gorev, tarih, mod: 'tamamla', gecikmeli: !pencere.olur })}
                      className="mt-2 text-xs font-semibold text-brand-700 hover:text-brand-800 border border-brand-300 hover:bg-brand-50 rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" /> Yine de yapacağım
                    </button>
                  )}
                </div>
              )}

              {acik && !onayda && (
                <div className="mt-3">
                  {!pencere.olur && pencere.sebep === 'erken' && (
                    <div className="text-xs mb-2 rounded-lg p-2 bg-gray-100 text-gray-600">
                      ⏳ {pencere.mesaj}
                    </div>
                  )}
                  <div className="flex gap-2">
                    {pencere.olur && (
                      <button
                        onClick={() => setModal({ gorev, tarih, mod: 'tamamla' })}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold py-2 rounded-lg flex items-center justify-center gap-1.5"
                      >
                        <CheckCircle2 className="w-4 h-4" /> Tamamladım
                      </button>
                    )}
                    {!pencere.olur && pencere.sebep === 'gecti' && (
                      <button
                        onClick={() => setModal({ gorev, tarih, mod: 'tamamla', gecikmeli: true })}
                        className="flex-1 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold py-2 rounded-lg flex items-center justify-center gap-1.5"
                      >
                        <CheckCircle2 className="w-4 h-4" /> Yine de tamamla
                      </button>
                    )}
                    <button
                      onClick={() => setModal({ gorev, tarih, mod: 'yapilamadi' })}
                      className={`${(pencere.olur || pencere.sebep === 'gecti') ? 'flex-1' : 'w-full'} border border-red-300 text-red-700 hover:bg-red-50 text-sm font-semibold py-2 rounded-lg flex items-center justify-center gap-1.5`}
                    >
                      <XCircle className="w-4 h-4" /> Yapamadım
                    </button>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      <GorevTamamlaModal
        acik={!!modal}
        gorev={modal?.gorev ?? null}
        personelId={personel.id}
        tarih={modal?.tarih ?? ''}
        mod={modal?.mod ?? 'tamamla'}
        gecikmeli={modal?.gecikmeli}
        onKapat={() => setModal(null)}
        onKaydedildi={() => { setModal(null); yukle() }}
      />
    </div>
  )
}
